/**
 * Mount Rigi Fixed Sticky Header
 * Version 6.2
 *
 * Reserviert dauerhaft Platz für den fixierten Header
 * und das höchste Desktop-Dropdown.
 *
 * Dadurch springt der Seiteninhalt beim Öffnen und
 * Schliessen eines Dropdowns nicht mehr.
 */

(() => {
  const SHELL_SELECTOR = '[data-mr-header-shell]';
  const SPACER_SELECTOR = '[data-mr-header-spacer]';
  const DROPDOWN_SELECTOR = '[data-mr-dropdown]';
  const DESKTOP_BREAKPOINT = 990;

  const initializeStickyHeader = (shell) => {
    if (!(shell instanceof HTMLElement)) return;

    if (
      shell.dataset.mrStickyHeaderInitialized ===
      'true'
    ) {
      return;
    }

    shell.dataset.mrStickyHeaderInitialized = 'true';

    const stickyEnabled =
      shell.dataset.mrStickyEnabled === 'true';

    const spacer =
      shell.parentElement?.querySelector(
        SPACER_SELECTOR
      ) ||
      (
        shell.nextElementSibling?.matches?.(
          SPACER_SELECTOR
        )
          ? shell.nextElementSibling
          : document.querySelector(
              SPACER_SELECTOR
            )
      );

    let reservedHeight = 0;
    let previousViewportWidth =
      window.innerWidth;

    const getMaximumDropdownHeight = () => {
      if (
        window.innerWidth <
        DESKTOP_BREAKPOINT
      ) {
        return 0;
      }

      let maximumHeight = 0;

      shell
        .querySelectorAll(DROPDOWN_SELECTOR)
        .forEach((dropdown) => {
          if (
            !(dropdown instanceof HTMLElement)
          ) {
            return;
          }

          const height = Math.ceil(
            dropdown.getBoundingClientRect().height
          );

          maximumHeight = Math.max(
            maximumHeight,
            height
          );
        });

      return maximumHeight;
    };

    const calculateReservedHeight = (
      forceReset = false
    ) => {
      if (!stickyEnabled) {
        return 0;
      }

      const shellHeight = Math.ceil(
        shell.getBoundingClientRect().height
      );

      const dropdownHeight =
        getMaximumDropdownHeight();

      const measuredHeight =
        shellHeight + dropdownHeight;

      /*
       * Während Scrollen und Hover darf der reservierte
       * Platz niemals kleiner werden.
       *
       * Nur bei einer echten Änderung der Fensterbreite
       * wird die Höhe neu berechnet.
       */
      if (forceReset) {
        reservedHeight = measuredHeight;
      } else {
        reservedHeight = Math.max(
          reservedHeight,
          measuredHeight
        );
      }

      return reservedHeight;
    };

    const applyHeight = (
      forceReset = false
    ) => {
      if (!stickyEnabled) {
        document.documentElement.style.removeProperty(
          '--mr-header-fixed-height'
        );

        shell.style.removeProperty(
          '--mr-header-fixed-height'
        );

        if (spacer instanceof HTMLElement) {
          spacer.style.height = '0px';
          spacer.style.minHeight = '0px';
        }

        return;
      }

      const height =
        calculateReservedHeight(forceReset);

      document.documentElement.style.setProperty(
        '--mr-header-fixed-height',
        `${height}px`
      );

      shell.style.setProperty(
        '--mr-header-fixed-height',
        `${height}px`
      );

      if (spacer instanceof HTMLElement) {
        spacer.style.height = `${height}px`;
        spacer.style.minHeight = `${height}px`;
      }
    };

    const scheduleUpdate = (
      forceReset = false
    ) => {
      window.requestAnimationFrame(() => {
        applyHeight(forceReset);
      });
    };

    applyHeight(true);

    window.addEventListener(
      'load',
      () => scheduleUpdate(true),
      { once: true }
    );

    window.addEventListener(
      'resize',
      () => {
        const currentViewportWidth =
          window.innerWidth;

        const widthChanged =
          currentViewportWidth !==
          previousViewportWidth;

        previousViewportWidth =
          currentViewportWidth;

        scheduleUpdate(widthChanged);
      },
      { passive: true }
    );

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => {
        scheduleUpdate(false);
      });

      observer.observe(shell);

      shell
        .querySelectorAll(DROPDOWN_SELECTOR)
        .forEach((dropdown) => {
          if (
            dropdown instanceof HTMLElement
          ) {
            observer.observe(dropdown);
          }
        });
    }

    document.fonts?.ready
      ?.then(() => scheduleUpdate(true))
      .catch(() => {});
  };

  const initializeAll = () => {
    document
      .querySelectorAll(SHELL_SELECTOR)
      .forEach(initializeStickyHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAll,
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initializeAll
  );
})();
